import { useNavigate } from "Navigation/useNavigate";
import { isIOS } from "Utils/Platform/PlatformUtils";
import { useEffect } from "react";

declare global {
  interface Window {
    __mooneNativeNavigate?: (path: string) => void;
  }
}

/**
 * Exposes a client-side navigation hook for the native iOS shell.
 *
 * After an external bank flow, the shell catches the moone:// bounce and needs to land the user on
 * the return page (e.g. /transaction-detail/:id). A full webView.load() there reloads the entire SPA
 * and briefly shows the stale page the user started from. Instead, the shell calls this hook to
 * navigate client-side — instant, no reload, no stale flash. The shell falls back to webView.load()
 * if the hook is absent (e.g. cold start before the SPA has mounted).
 *
 * Renders nothing. Must live inside the router so useNavigate is available, and only registers on iOS
 * — the only shell whose native handler (handleAppSchemeUrl) calls the hook. No-op on web and Android.
 */
export const NativeNavigationBridge = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isIOS()) {
      return;
    }

    window.__mooneNativeNavigate = (path: string) => navigate(path);

    return () => {
      window.__mooneNativeNavigate = undefined;
    };
  }, [navigate]);

  return null;
};
